// src/pages/whitelabel/CustomDomainPage.js
import { useState, useEffect } from 'react';
import { domainController } from '../../controllers/domainController';
import { getEnvironmentConfig, getEnvironmentMessage } from '../../utils/environmentHelper';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, 
  ChevronUp, ChevronDown, Globe, CheckCircle, XCircle, 
  RefreshCw, Shield, Server, ExternalLink, Copy, Settings,
  AlertTriangle, Clock
} from 'lucide-react';

const CustomDomainPage = () => {
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDNSModal, setShowDNSModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [formData, setFormData] = useState({
    subdomain: '',
    customDomain: '',
    domainType: 'subdomain',
    redirectPath: '/dashboard',
    sslEnabled: true,
    isActive: true,
    allowedModules: []
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const itemsPerPage = 10;

  // Get environment configuration
  const envConfig = getEnvironmentConfig();
  const isLocalhost = envConfig.isLocal;
  const baseDomain = envConfig.baseDomain;

  const moduleOptions = [
    { value: 'crm', label: 'CRM' },
    { value: 'hrms', label: 'HRMS' },
    { value: 'projects', label: 'Projects' },
    { value: 'sales', label: 'Sales' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'mdm', label: 'Device Management' },
    { value: 'reports', label: 'Reports' },
    { value: 'settings', label: 'Settings' },
    { value: 'whitelabel', label: 'White Label' },
    { value: 'communication', label: 'Communication' },
  ];

  useEffect(() => { 
    fetchDomains(); 
  }, []);

  useEffect(() => {
    let result = [...domains];
    
    if (searchQuery) {
      result = result.filter(d =>
        (d.subdomain || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.customDomain || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (d.domainId || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      if (filterStatus === 'Active') {
        result = result.filter(d => d.isActive && d.dnsVerified);
      } else if (filterStatus === 'Inactive') {
        result = result.filter(d => !d.isActive);
      } else if (filterStatus === 'Pending') {
        result = result.filter(d => d.isActive && !d.dnsVerified);
      }
    }
    
    if (filterType !== 'All') {
      result = result.filter(d => d.domainType === filterType);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredDomains(result);
    setCurrentPage(1);
  }, [domains, searchQuery, filterStatus, filterType, sortField, sortDirection]);

  const fetchDomains = async () => {
    setLoading(true);
    const result = await domainController.getAll();
    if (result.success) {
      setDomains(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    setFormErrors([]);
  };

  const handleModuleToggle = (moduleValue) => {
    setFormData(prev => ({
      ...prev,
      allowedModules: prev.allowedModules.includes(moduleValue)
        ? prev.allowedModules.filter(m => m !== moduleValue)
        : [...prev.allowedModules, moduleValue]
    }));
  };

  const handleAdd = () => {
    setFormData({
      subdomain: '',
      customDomain: '',
      domainType: 'subdomain',
      redirectPath: '/dashboard',
      sslEnabled: true,
      isActive: true,
      allowedModules: []
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await domainController.create(formData);
    if (result.success) {
      setShowAddModal(false);
      fetchDomains();
      alert('✅ Domain created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleEdit = (domain) => {
    setSelectedDomain(domain);
    setFormData({
      subdomain: domain.subdomain || '',
      customDomain: domain.customDomain || '',
      domainType: domain.domainType || 'subdomain',
      redirectPath: domain.redirectPath || '/dashboard',
      sslEnabled: domain.sslEnabled ?? true,
      isActive: domain.isActive ?? true,
      allowedModules: domain.allowedModules || []
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);
    
    const result = await domainController.update(selectedDomain.id || selectedDomain._id, formData);
    if (result.success) {
      setShowEditModal(false);
      fetchDomains();
      alert('✅ Domain updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    setFormSubmitting(false);
  };

  const handleView = (domain) => {
    setSelectedDomain(domain);
    setShowViewModal(true);
  };

  const handleDelete = async (domain) => {
    try {
      const isConfirmed = window.confirm(
        `Delete domain "${domain.getFullDomain()}"? This action cannot be undone.`
      );
  
      if (!isConfirmed) return;
  
      const result = await domainController.delete(domain.id || domain._id);
  
      if (result?.success) {
        await fetchDomains();
        alert('✅ Domain deleted successfully!');
      } else {
        alert(result?.message || '❌ Failed to delete domain');
      }
    } catch (error) {
      console.error('Delete domain error:', error);
      alert('❌ Something went wrong while deleting domain');
    }
  };

  const handleVerifyDNS = async (domain) => {
    setVerifying(true);
    const result = await domainController.verifyDNS(domain.id || domain._id);
    if (result.success) {
      fetchDomains();
      alert(result.verified ? '✅ DNS verified successfully!' : '⚠️ DNS verification pending');
    } else {
      alert('Error: ' + result.message);
    }
    setVerifying(false);
  };

  const handleToggleStatus = async (domain) => {
    const result = await domainController.toggleStatus(domain.id || domain._id);
    if (result.success) {
      fetchDomains();
      alert('✅ Status updated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleGenerateSSL = async (domain) => {
    const result = await domainController.generateSSL(domain.id || domain._id);
    if (result.success) {
      fetchDomains();
      alert('✅ SSL certificate generated!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleViewDNS = async (domain) => {
    setSelectedDomain(domain);
    setShowDNSModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('📋 Copied to clipboard!');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const filters = {};
    if (filterStatus !== 'All') filters.status = filterStatus;
    if (filterType !== 'All') filters.domainType = filterType;
    
    const result = await domainController.export(filters, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `domains-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Domains exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSelectDomain = (domainId) => {
    setSelectedDomains(prev => 
      prev.includes(domainId) 
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDomains.length === currentDomains.length && currentDomains.length > 0) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(currentDomains.map(d => d.id || d._id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDomains.length === 0) {
      alert('Please select domains to delete');
      return;
    }
    
    const confirmed = window.confirm(`Delete ${selectedDomains.length} selected domains?`);
    if (!confirmed) return;
    
    const result = await domainController.bulkDelete(selectedDomains);
    if (result.success) {
      setSelectedDomains([]);
      fetchDomains();
      alert('✅ Domains deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDomains = filteredDomains.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDomains.length / itemsPerPage);

  const stats = {
    total: domains.length,
    active: domains.filter(d => d.isActive && d.dnsVerified).length,
    pending: domains.filter(d => d.isActive && !d.dnsVerified).length,
    inactive: domains.filter(d => !d.isActive).length,
    sslEnabled: domains.filter(d => d.sslEnabled).length
  };

  const styles = {
    container: { padding: 0 },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '24px', 
      flexWrap: 'wrap', 
      gap: '16px' 
    },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '14px', color: '#64748b', marginTop: '4px' },
    button: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      padding: '10px 20px', 
      backgroundColor: '#3b82f6', 
      color: '#fff', 
      border: 'none', 
      borderRadius: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    secondaryButton: { 
      backgroundColor: '#fff', 
      color: '#1e293b', 
      border: '1px solid #e2e8f0' 
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: '#fff'
    },
    successButton: {
      backgroundColor: '#10b981',
      color: '#fff'
    },
    statsBar: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px', 
      marginBottom: '24px' 
    },
    statCard: { 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer'
    },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '20px', 
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { 
      width: '100%', 
      padding: '10px 10px 10px 40px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    searchIcon: { 
      position: 'absolute', 
      left: '12px', 
      top: '50%', 
      transform: 'translateY(-50%)' 
    },
    select: { 
      padding: '10px 16px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      backgroundColor: '#fff', 
      cursor: 'pointer' 
    },
    card: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
      overflow: 'hidden' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      backgroundColor: '#f8fafc', 
      padding: '16px', 
      textAlign: 'left', 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#475569', 
      borderBottom: '1px solid #e2e8f0', 
      cursor: 'pointer', 
      userSelect: 'none' 
    },
    td: { 
      padding: '16px', 
      borderBottom: '1px solid #e2e8f0', 
      fontSize: '14px', 
      color: '#1e293b' 
    },
    badge: { 
      display: 'inline-block', 
      padding: '4px 12px', 
      borderRadius: '12px', 
      fontSize: '12px', 
      fontWeight: '500' 
    },
    actionButton: { 
      padding: '6px', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderRadius: '6px', 
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    pagination: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '20px', 
      borderTop: '1px solid #e2e8f0' 
    },
    modal: { 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      padding: '20px' 
    },
    modalContent: { 
      backgroundColor: '#fff', 
      borderRadius: '12px', 
      width: '100%', 
      maxWidth: '800px', 
      maxHeight: '90vh', 
      overflow: 'auto' 
    },
    modalHeader: { 
      padding: '20px 24px', 
      borderBottom: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center' 
    },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { 
      display: 'block', 
      marginBottom: '8px', 
      fontSize: '14px', 
      fontWeight: '500', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none' 
    },
    textarea: { 
      width: '100%', 
      padding: '10px 12px', 
      border: '1px solid #e2e8f0', 
      borderRadius: '8px', 
      fontSize: '14px', 
      outline: 'none', 
      minHeight: '80px', 
      resize: 'vertical' 
    },
    errorList: { 
      backgroundColor: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: '8px', 
      padding: '12px 16px', 
      marginBottom: '20px' 
    },
    errorItem: { 
      color: '#dc2626', 
      fontSize: '14px', 
      marginBottom: '4px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px' 
    },
    modalFooter: { 
      padding: '16px 24px', 
      borderTop: '1px solid #e2e8f0', 
      display: 'flex', 
      justifyContent: 'flex-end', 
      gap: '12px' 
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    infoSection: {
      marginTop: '20px',
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px'
    },
    moduleGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginTop: '12px'
    },
    moduleCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    dnsRecord: {
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      marginBottom: '8px',
      fontFamily: 'monospace',
      fontSize: '13px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Custom Domain Management</h1>
          <p style={styles.subtitle}>Configure and manage subdomains and custom domains</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {selectedDomains.length > 0 && (
            <button 
              style={{ ...styles.button, ...styles.dangerButton }} 
              onClick={handleBulkDelete}
            >
              <Trash2 size={18} /> Delete ({selectedDomains.length})
            </button>
          )}
          <button 
            style={{ ...styles.button, ...styles.secondaryButton }} 
            onClick={handleExport}
          >
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Add Domain
          </button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard} onClick={() => setFilterStatus('All')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Domains</div>
            <Globe size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard} onClick={() => setFilterStatus('Active')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Active</div>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.active}</div>
        </div>
        <div style={styles.statCard} onClick={() => setFilterStatus('Pending')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Pending Verification</div>
            <Clock size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.pending}</div>
        </div>
        <div style={styles.statCard} onClick={() => setFilterStatus('Inactive')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Inactive</div>
            <XCircle size={24} color="#64748b" />
          </div>
          <div style={{ ...styles.statValue, color: '#64748b' }}>{stats.inactive}</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>SSL Enabled</div>
            <Shield size={24} color="#8b5cf6" />
          </div>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{stats.sslEnabled}</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search domains..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        <select 
          style={styles.select} 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending Verification</option>
          <option value="Inactive">Inactive</option>
        </select>
        <select 
          style={styles.select} 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="subdomain">Subdomain</option>
          <option value="custom">Custom Domain</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <RefreshCw className="spin" size={32} style={{ marginBottom: '12px' }} />
            <div>Loading domains...</div>
          </div>
        ) : currentDomains.length === 0 ? (
          <div style={{ padding: '60px 40px', textAlign: 'center', color: '#64748b' }}>
            <Globe size={64} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
              No domains found
            </h3>
            <p style={{ marginBottom: '24px' }}>
              {searchQuery || filterStatus !== 'All' || filterType !== 'All' 
                ? 'Try adjusting your filters' 
                : 'Get started by adding your first domain'}
            </p>
            {!searchQuery && filterStatus === 'All' && filterType === 'All' && (
              <button style={styles.button} onClick={handleAdd}>
                <Plus size={18} /> Add Domain
              </button>
            )}
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: '50px' }}>
                    <input 
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedDomains.length === currentDomains.length && currentDomains.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={styles.th} onClick={() => handleSort('domainId')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      ID {sortField === 'domainId' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('subdomain')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Domain {sortField === 'subdomain' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Redirect</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>SSL</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentDomains.map((domain) => (
                  <tr key={domain.id || domain._id} style={{ cursor: 'pointer' }}>
                    <td style={styles.td}>
                      <input 
                        type="checkbox"
                        style={styles.checkbox}
                        checked={selectedDomains.includes(domain.id || domain._id)}
                        onChange={() => handleSelectDomain(domain.id || domain._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                        {domain.domainId}
                      </div>
                    </td>
                    <td style={styles.td} onClick={() => handleView(domain)}>
                      <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Globe size={16} color="#64748b" />
                        {domain.getFullDomain()}
                      </div>
                      {domain.allowedModules && domain.allowedModules.length > 0 && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          {domain.allowedModules.length} module{domain.allowedModules.length !== 1 ? 's' : ''} allowed
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{ 
                        ...styles.badge, 
                        backgroundColor: domain.domainType === 'subdomain' ? '#dbeafe' : '#fce7f3', 
                        color: domain.domainType === 'subdomain' ? '#1e40af' : '#9f1239' 
                      }}>
                        {domain.domainType === 'subdomain' ? 'Subdomain' : 'Custom'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#64748b' }}>
                        {domain.redirectPath}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ 
                          ...styles.badge, 
                          backgroundColor: `${domain.getStatusColor()}20`, 
                          color: domain.getStatusColor() 
                        }}>
                          {domain.getStatusText()}
                        </span>
                        {domain.needsVerification() && (
                          <button
                            style={{ ...styles.button, ...styles.secondaryButton, padding: '4px 8px', fontSize: '11px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVerifyDNS(domain);
                            }}
                            disabled={verifying}
                          >
                            {verifying ? 'Verifying...' : 'Verify DNS'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ 
                        ...styles.badge, 
                        backgroundColor: `${domain.getSSLStatusColor()}20`, 
                        color: domain.getSSLStatusColor() 
                      }}>
                        <Shield size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        {domain.getSSLStatusText()}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          style={styles.actionButton} 
                          onClick={(e) => { e.stopPropagation(); handleView(domain); }} 
                          title="View Details"
                        >
                          <Eye size={18} color="#64748b" />
                        </button>
                        <button 
                          style={styles.actionButton} 
                          onClick={(e) => { e.stopPropagation(); handleViewDNS(domain); }} 
                          title="View DNS Records"
                        >
                          <Server size={18} color="#3b82f6" />
                        </button>
                        <button 
                          style={styles.actionButton} 
                          onClick={(e) => { e.stopPropagation(); handleEdit(domain); }} 
                          title="Edit"
                        >
                          <Edit2 size={18} color="#10b981" />
                        </button>
                        <button 
                          style={styles.actionButton} 
                          onClick={(e) => { e.stopPropagation(); handleDelete(domain); }} 
                          title="Delete"
                        >
                          <Trash2 size={18} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredDomains.length)} of {filteredDomains.length} domains
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {showAddModal ? 'Add New Domain' : 'Edit Domain'}
              </h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Domain Type *</label>
                  <select 
                    name="domainType" 
                    style={styles.input} 
                    value={formData.domainType} 
                    onChange={handleInputChange}
                    required
                  >
                    <option value="subdomain">Subdomain (e.g., admin.yourapp.com)</option>
                    <option value="custom">Custom Domain (e.g., yourdomain.com)</option>
                  </select>
                </div>

                {formData.domainType === 'subdomain' ? (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Subdomain Name *</label>
                    <div style={{ position: 'relative' }}>
                      <input 
                        type="text" 
                        name="subdomain" 
                        style={{ ...styles.input, paddingRight: isLocalhost ? '160px' : '140px' }} 
                        value={formData.subdomain} 
                        onChange={handleInputChange} 
                        placeholder="admin" 
                        pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
                        required
                      />
                      <span style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#64748b',
                        fontSize: '14px'
                      }}>
                        .{baseDomain}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      {isLocalhost 
                        ? 'Will create: http://' + (formData.subdomain || 'subdomain') + '.' + baseDomain
                        : 'Only lowercase letters, numbers, and hyphens (min 3 characters)'}
                    </div>
                  </div>
                ) : (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Custom Domain *</label>
                    <input 
                      type="text" 
                      name="customDomain" 
                      style={styles.input} 
                      value={formData.customDomain} 
                      onChange={handleInputChange} 
                      placeholder="yourdomain.com" 
                      required
                    />
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      Enter your full custom domain name
                    </div>
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Redirect Path *</label>
                  <input 
                    type="text" 
                    name="redirectPath" 
                    style={styles.input} 
                    value={formData.redirectPath} 
                    onChange={handleInputChange} 
                    placeholder="/dashboard" 
                    required
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    The page users will be redirected to when visiting this domain
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Allowed Modules</label>
                  <div style={styles.moduleGrid}>
                    {moduleOptions.map(module => (
                      <label 
                        key={module.value}
                        style={{
                          ...styles.moduleCheckbox,
                          backgroundColor: formData.allowedModules.includes(module.value) ? '#eff6ff' : '#fff',
                          borderColor: formData.allowedModules.includes(module.value) ? '#3b82f6' : '#e2e8f0'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.allowedModules.includes(module.value)}
                          onChange={() => handleModuleToggle(module.value)}
                          style={{ margin: 0 }}
                        />
                        <span style={{ fontSize: '13px' }}>{module.label}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                    Leave empty to allow all modules
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        name="sslEnabled"
                        checked={formData.sslEnabled}
                        onChange={handleInputChange}
                        disabled={isLocalhost}
                      />
                      Enable SSL Certificate
                    </label>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      {isLocalhost 
                        ? '🔒 SSL disabled for localhost' 
                        : 'Automatically generate and manage SSL certificates'}
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      Activate Domain
                    </label>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      Make this domain immediately accessible
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button 
                  type="button" 
                  style={{ ...styles.button, ...styles.secondaryButton }} 
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }} 
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Domain' : 'Update Domain')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedDomain && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Domain Details</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Domain ID</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedDomain.domainId}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Full Domain</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {selectedDomain.getFullDomain()}
                    <button
                      style={styles.actionButton}
                      onClick={() => copyToClipboard(selectedDomain.getFullDomain())}
                      title="Copy"
                    >
                      <Copy size={16} color="#64748b" />
                    </button>
                    <a
                      href={`https://${selectedDomain.getFullDomain()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.actionButton}
                      title="Visit"
                    >
                      <ExternalLink size={16} color="#3b82f6" />
                    </a>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Type</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: selectedDomain.domainType === 'subdomain' ? '#dbeafe' : '#fce7f3', 
                    color: selectedDomain.domainType === 'subdomain' ? '#1e40af' : '#9f1239' 
                  }}>
                    {selectedDomain.domainType === 'subdomain' ? 'Subdomain' : 'Custom Domain'}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Redirect Path</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', fontFamily: 'monospace' }}>
                    {selectedDomain.redirectPath}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedDomain.getStatusColor()}20`, 
                    color: selectedDomain.getStatusColor() 
                  }}>
                    {selectedDomain.getStatusText()}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>SSL Status</div>
                  <span style={{ 
                    ...styles.badge, 
                    backgroundColor: `${selectedDomain.getSSLStatusColor()}20`, 
                    color: selectedDomain.getSSLStatusColor() 
                  }}>
                    {selectedDomain.getSSLStatusText()}
                  </span>
                </div>

                {selectedDomain.sslExpiryDate && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>SSL Expiry</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                      {new Date(selectedDomain.sslExpiryDate).toLocaleDateString()}
                    </div>
                  </div>
                )}

                {selectedDomain.lastVerifiedAt && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Last Verified</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                      {new Date(selectedDomain.lastVerifiedAt).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {selectedDomain.allowedModules && selectedDomain.allowedModules.length > 0 && (
                <div style={styles.infoSection}>
                  <div style={styles.sectionTitle}>Allowed Modules</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedDomain.allowedModules.map(module => {
                      const moduleInfo = moduleOptions.find(m => m.value === module);
                      return (
                        <span key={module} style={{ 
                          ...styles.badge, 
                          backgroundColor: '#eff6ff', 
                          color: '#1e40af' 
                        }}>
                          {moduleInfo?.label || module}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b' }}>
                <div>Created: {new Date(selectedDomain.createdAt).toLocaleString()}</div>
                <div style={{ marginTop: '4px' }}>Last Updated: {new Date(selectedDomain.updatedAt).toLocaleString()}</div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => handleViewDNS(selectedDomain)}
              >
                <Server size={16} /> View DNS
              </button>
              {selectedDomain.needsVerification() && (
                <button 
                  style={styles.button} 
                  onClick={() => {
                    setShowViewModal(false);
                    handleVerifyDNS(selectedDomain);
                  }}
                >
                  <RefreshCw size={16} /> Verify DNS
                </button>
              )}
              <button 
                style={{ ...styles.button, ...styles.successButton }} 
                onClick={() => { setShowViewModal(false); handleEdit(selectedDomain); }}
              >
                <Edit2 size={16} /> Edit Domain
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DNS Records Modal */}
      {showDNSModal && selectedDomain && (
        <div style={styles.modal} onClick={() => setShowDNSModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>DNS Configuration</h2>
              <button 
                style={{ ...styles.actionButton, padding: '8px' }} 
                onClick={() => setShowDNSModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '12px' }}>
                <AlertTriangle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '14px', color: '#92400e' }}>
                  <strong>Important:</strong> Add these DNS records to your domain provider's control panel to verify ownership and enable SSL.
                </div>
              </div>

              <div style={styles.infoSection}>
                <div style={styles.sectionTitle}>Required DNS Records</div>
                
                {selectedDomain.domainType === 'subdomain' ? (
                  <div style={styles.dnsRecord}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Type:</strong> CNAME
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Name:</strong> {selectedDomain.subdomain}
                      <button
                        style={{ ...styles.actionButton, marginLeft: '8px' }}
                        onClick={() => copyToClipboard(selectedDomain.subdomain)}
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <div>
                      <strong>Value:</strong> yourapp.com
                      <button
                        style={{ ...styles.actionButton, marginLeft: '8px' }}
                        onClick={() => copyToClipboard('yourapp.com')}
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.dnsRecord}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Type:</strong> A
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Name:</strong> @
                      </div>
                      <div>
                        <strong>Value:</strong> 123.456.789.0 (Your server IP)
                        <button
                          style={{ ...styles.actionButton, marginLeft: '8px' }}
                          onClick={() => copyToClipboard('123.456.789.0')}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>

                    <div style={styles.dnsRecord}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Type:</strong> CNAME
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Name:</strong> www
                      </div>
                      <div>
                        <strong>Value:</strong> {selectedDomain.customDomain}
                        <button
                          style={{ ...styles.actionButton, marginLeft: '8px' }}
                          onClick={() => copyToClipboard(selectedDomain.customDomain)}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {selectedDomain.sslEnabled && (
                  <div style={styles.dnsRecord}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Type:</strong> TXT (For SSL Validation)
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Name:</strong> _acme-challenge.{selectedDomain.domainType === 'subdomain' ? selectedDomain.subdomain : '@'}
                    </div>
                    <div>
                      <strong>Value:</strong> (Will be provided during SSL generation)
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
                💡 <strong>Tip:</strong> DNS propagation can take up to 48 hours. Use the "Verify DNS" button to check if your records are properly configured.
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={{ ...styles.button, ...styles.secondaryButton }} 
                onClick={() => setShowDNSModal(false)}
              >
                Close
              </button>
              <button 
                style={styles.button} 
                onClick={() => {
                  setShowDNSModal(false);
                  handleVerifyDNS(selectedDomain);
                }}
                disabled={verifying}
              >
                <RefreshCw size={16} /> {verifying ? 'Verifying...' : 'Verify Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDomainPage;